import React, { Component, createRef } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Route, RouteComponentProps, Router, Switch } from "react-router-dom";
import styled from "styled-components";
import { analyticsFEA } from "src/AnalyticsFEA";
import Modal from "src/components/Modal";
import UpgradesReview from "src/containers/UpgradesReview";
import { Account } from "Types/api/Account";
import { CreditAppStatuses } from "Types/creditApp";
import { DealState, OnlineShopperState, PaymentTypeOptions, VehicleState } from "Types/state";
import { TradeIn } from "Types/TradeIn";
import "./ContinueDeal.scss";
import ContinueDealHeader from "./ContinueDealHeader";
import CreditApp from "./CreditApp/CreditApp";
import StepWrapper from "./CreditApp/CreditApp/StepWrapper";
import CustomizeVehicle from "./CustomizeVehicle/CustomizeVehicle";
import ScheduleAppointmentSuccess from "./ScheduleAppointmentSuccess";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import StepTwo from "./StepTwo";
import { calculateNextStep, Steps } from "./utils";
import VehicleInfo, { VehicleInfoProps } from "./VehicleInfo";

const StepsContainer = styled("div")`
    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 2em;
    }
`;

const WithMargin = (Comp: any) => (props: any): JSX.Element => (
    <StepsContainer>
        <Comp {...props} />
    </StepsContainer>
);

export interface ContinueDealProps extends RouteComponentProps {
    hasAvailableAccessories: boolean;
    account: Account;
    creditAppStatus: CreditAppStatuses;
    deal: DealState;
    isDrawerOpen: OnlineShopperState["isDrawerOpen"];
    tradePending: TradeIn;
    vehicle: VehicleState;
    openPaymentPlanReviewModal?: (vin: string) => void;
    resetCreditApp?: () => void;
    skipCreditApplication?: () => void;
    toggleGPC: () => void;
    getAccessories: (uuid: string, vin: string, paymentType: PaymentTypeOptions) => void;
}

interface State {
    accessoriesChanged: boolean;
    accessoriesReviewModalOpened: boolean;
    accessoriesSkipped: boolean;
}

class ContinueDeal extends Component<ContinueDealProps & WithTranslation, State> {
    public state: Readonly<State> = {
        accessoriesChanged: false,
        accessoriesReviewModalOpened: false,
        accessoriesSkipped: false,
    };
    private creditAppContainerWrapper: any;
    private customizeVehicleWrapper: (props: any) => JSX.Element;
    private scheduleAppointmentWrapper: (props: any) => JSX.Element;
    private contentContainerRef = createRef<HTMLDivElement>();
    private dropHistoryListener: () => void;

    public componentDidMount() {
        this.props.getAccessories(this.props.deal.uuid!, this.props.vehicle.vin, this.props.vehicle.payments.selected);
        this.creditAppContainerWrapper = StepWrapper(CreditApp, this.handleOnCreditAppBack);
        this.customizeVehicleWrapper = WithMargin(this.renderCustomizeVehicle(this.props.vehicle.vin));
        this.scheduleAppointmentWrapper = WithMargin(ScheduleAppointmentSuccess);

        this.handleScroll();
        this.dropHistoryListener = this.props.history.listen((location, action) => {
            this.handleScroll();
        });

        if (this.props.isDrawerOpen) {
            this.props.toggleGPC();
        }
    }

    public componentWillUnmount() {
        this.dropHistoryListener();
    }

    public goToGarage() {
        this.props.history.push("/my-garage");
    }

    public viewCustomizedPaymentPlan() {
        this.props.openPaymentPlanReviewModal && this.props.openPaymentPlanReviewModal(this.props.vehicle.vin);
    }

    public handleGoToCreditApp = () => {
        analyticsFEA.engine.hardPullCreditAppStart(this.props.deal.uuid as string, [this.props.vehicle]);
        this.props.resetCreditApp && this.props.resetCreditApp();
        this.props.history.push("/continue-deal/credit-application");
    };

    public handleOnCreditAppBack = () => {
        this.props.history.push("/continue-deal");
    };

    public handleGoToSkipForNow = () => {
        analyticsFEA.engine.skipCreditApp();
        this.props.skipCreditApplication && this.props.skipCreditApplication();
    };

    public handleOpenAccessoriesReviewModal = () => {
        this.setState({
            accessoriesReviewModalOpened: true,
        });
    };

    public handleOnAccessoriesReviewModalClose = () => {
        this.setState({
            accessoriesReviewModalOpened: false,
        });
    };

    public handleAccessoriesChange = () => {
        this.setState({
            accessoriesChanged: true,
        });
    };

    public handleGoToCustomizeYourVehicle = () => {
        analyticsFEA.engine.exploreUpgrades();
        this.props.history.push("/continue-deal/customize-vehicle");
    };

    public handleOnAddMoreUpgradesClick = () => {
        this.setState({
            accessoriesReviewModalOpened: false,
        });

        this.props.history.push("/continue-deal/customize-vehicle");
    };

    public handleOnCustomizeYourVehicleBack = () => {
        this.setState({
            accessoriesSkipped: false,
        });

        this.props.history.push("/continue-deal");
    };

    public handleOnCustomizeYourVehicleNext = () => {
        analyticsFEA.engine.FAndIFinish(this.props.deal.uuid || "", [this.props.vehicle]);

        this.setState({
            accessoriesChanged: false,
            accessoriesSkipped: true,
        });
        this.props.history.push("/continue-deal");
    };

    public handleOnCustomizeYourVehicleSkip = () => {
        analyticsFEA.engine.skipUpgrades();
        analyticsFEA.engine.FAndIFinish(this.props.deal.uuid || "", [this.props.vehicle]);

        this.setState({
            accessoriesChanged: false,
            accessoriesSkipped: true,
        });
        this.props.history.push("/continue-deal");
    };

    public renderCustomizeVehicle = (vin: string) => () => (
        <CustomizeVehicle
            vin={vin}
            onAccessoriesChange={this.handleAccessoriesChange}
            onBackClick={this.handleOnCustomizeYourVehicleBack}
            onNextClick={this.handleOnCustomizeYourVehicleNext}
            onSkipClick={this.handleOnCustomizeYourVehicleSkip}
        />
    );

    public render() {
        const { account, creditAppStatus, deal, tradePending, vehicle, hasAvailableAccessories } = this.props;
        const { accessoriesSkipped } = this.state;

        const vehicleInfoProps = {
            account,
            availableAccessories: vehicle.accessories,
            deal,
            onGoToGarage: () => this.goToGarage(),
            onViewCustomizedPaymentPlan: () => this.viewCustomizedPaymentPlan(),
            tradePending,
            vehicle,
        } as VehicleInfoProps;

        const currentStep = calculateNextStep(hasAvailableAccessories, creditAppStatus, accessoriesSkipped, vehicle);

        return (
            <div className="ContinueDeal__container" ref={this.contentContainerRef}>
                <VehicleInfo {...vehicleInfoProps} />

                <div className="ContinueDeal__main">
                    <div className="ContinueDeal__main--container">
                        <Router history={this.props.history}>
                            <Switch>
                                <Route
                                    exact
                                    path="/continue-deal/credit-application"
                                    component={this.creditAppContainerWrapper}
                                />
                                <Route
                                    exact
                                    path="/continue-deal/schedule-appointment"
                                    component={this.scheduleAppointmentWrapper}
                                />
                                <Route
                                    exact
                                    path="/continue-deal/customize-vehicle"
                                    render={this.customizeVehicleWrapper}
                                />
                                <Route
                                    path="/"
                                    render={WithMargin(this.renderSteps(hasAvailableAccessories, currentStep))}
                                />
                            </Switch>
                        </Router>
                    </div>
                </div>

                <Modal
                    className={"ContinueDeal__UpgradesReviewModal"}
                    open={this.state.accessoriesReviewModalOpened}
                    closeModal={this.handleOnAccessoriesReviewModalClose}
                >
                    <UpgradesReview onAddMoreUpgrades={this.handleOnAddMoreUpgradesClick} vin={vehicle.vin} />
                </Modal>
            </div>
        );
    }

    public handleScroll = () => {
        setTimeout(() => {
            if (this.contentContainerRef && this.contentContainerRef.current) {
                this.contentContainerRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }, 300);
    };

    private renderSteps = (hasAvailableAccessories: boolean, currentStep: Steps) => () => {
        const { account, deal, vehicle, creditAppStatus, t } = this.props;

        return (
            <div className="ContinueDeal__steps">
                <ContinueDealHeader type="secondary" deal={deal} />
                {hasAvailableAccessories ? (
                    <>
                        <StepOne
                            active={currentStep === 1}
                            status={creditAppStatus}
                            className="ContinueDeal__step"
                            onGoToCreditApp={this.handleGoToCreditApp}
                            onSkipForNow={this.handleGoToSkipForNow}
                        />
                        <StepTwo
                            active={currentStep === 2}
                            done={currentStep > 2}
                            className="ContinueDeal__step"
                            onEditUpgrades={this.handleOpenAccessoriesReviewModal}
                            onExploreUpgrades={this.handleGoToCustomizeYourVehicle}
                            vehicle={vehicle}
                        />
                        <StepThree
                            title={account.account_disclaimers?.schedule_delivery_pickup_title}
                            text_active={account.account_disclaimers?.schedule_delivery_pickup_text_active}
                            text_inactive={account.account_disclaimers?.schedule_delivery_pickup_text_inactive}
                            active={currentStep === 3}
                            vehicle={vehicle}
                        />
                    </>
                ) : (
                    <>
                        <StepOne
                            active={currentStep === 1}
                            status={creditAppStatus}
                            className="ContinueDeal__step"
                            onGoToCreditApp={this.handleGoToCreditApp}
                            onSkipForNow={this.handleGoToSkipForNow}
                        />
                        <StepThree
                            stepName={t("ContinueDeal.stepTwo")}
                            title={account.account_disclaimers?.schedule_delivery_pickup_title}
                            text_active={account.account_disclaimers?.schedule_delivery_pickup_text_active}
                            text_inactive={account.account_disclaimers?.schedule_delivery_pickup_text_inactive}
                            active={currentStep === 2 || currentStep === 3}
                            vehicle={vehicle}
                        />
                    </>
                )}
            </div>
        );
    };
}

export default withTranslation()(ContinueDeal);
